<template>
  <b-card :header="$t('project.trendsExport.trendsPage')">
    <div v-if="errored" class="alert alert-danger">{{errored}}</div>
    <div v-if="informed" class="alert alert-success">{{informed}}</div>
    <div v-if="presetLoading" class="alert alert-info">{{ $t('project.graphViewer.presetLoading') }}</div>
    <h4>{{ $t('project.trendsExport.mainText') }}</h4>
    <ol>
      <li>{{ $t('project.trendsExport.step1') }}</li>
      <li>{{ $t('project.trendsExport.step2') }}</li>
      <li>{{ $t('project.trendsExport.step3') }}</li>
      <li>{{ $t('project.trendsExport.step4') }}</li>
    </ol>
    <p>{{ $t('project.trendsExport.infoText') }}</p>

    <div role="tablist" class="trends-tablist">
      <b-card no-body class="mb-1">
        <b-card-header header-tag="header" class="p-1" role="tab">
          <b-button
            block
            href="#"
            v-b-toggle.accordion-stations
            variant="info"
          >{{ $t('project.alarmsExport.selectStations') }}</b-button>
        </b-card-header>
        <b-collapse
          id="accordion-stations"
          visible
          accordion="accordion"
          v-model="accStations"
          role="tabpanel"
        >
          <b-card-body>
            <b-form-group>
              <b-button size="sm" class="mb-2" @click="selectAllStations()" >{{ $t('project.alarmsExport.selectAll') }}</b-button>
              <b-form-checkbox-group id="stations" v-model="selectedStations" stacked>
                <b-form-checkbox  v-for="(value, key) in stations" :key="key" :value="value.item1">{{ value.item2 }}</b-form-checkbox>
              </b-form-checkbox-group>
            </b-form-group>
            <b-button
              class="mt-3"
              block
              variant="success"
              @click="selectStations()"
            >{{ $t('project.alarmsExport.selectStationsConfirm') }}</b-button>
          </b-card-body>
        </b-collapse>
      </b-card>

      <b-card no-body class="mb-1">
        <b-card-header header-tag="header" class="p-1" role="tab">
          <b-button
            block
            href="#"
            v-b-toggle.accordion-presets
            variant="info"
          >{{ $t('project.alarmsExport.selectPreset') }}</b-button>
        </b-card-header>
        <b-collapse
          id="accordion-presets"
          visible
          accordion="accordion"
          v-model="accPresets"
          role="tabpanel"
        >
          <b-card-body>
            <b-form-group>
              <b-form-radio-group id="presets" v-model="selectedPreset" stacked>
                <b-form-radio v-for="(value, key) in presets" :key="key" :value="{ id: value.id, name: value.name, configuration: value.configuration}">{{ value.name }}</b-form-radio>
              </b-form-radio-group>
            </b-form-group>
            <b-button
              class="mt-3"
              block
              variant="success"
              @click="selectPreset()"
              :disabled="presetLoading"
            >{{ $t('project.alarmsExport.selectPresetConfirm') }}</b-button>
          </b-card-body>
        </b-collapse>
      </b-card>

      <b-card no-body class="mb-1">
        <b-card-header header-tag="header" class="p-1" role="tab">
          <b-button
            block
            href="#"
            v-b-toggle.accordion-trends
            variant="info"
          >{{ $t('project.trendsExport.viewTrends') }}</b-button>
        </b-card-header>
        <b-collapse id="accordion-trends" accordion="accordion" v-model="accTrends" role="tabpanel">
          <b-card-body class="p-1">
            <b-row>
              <b-col md="6" class="my-1">
                <b-form-group label-cols-sm="3" :label="$t('other.filter')" class="mb-0">
                  <b-input-group>
                    <b-form-input v-model="filter" :placeholder="$t('other.searchHolder')"></b-form-input>
                    <b-input-group-append>
                      <b-button :disabled="!filter" @click="filter = ''">{{ $t('other.clear') }}</b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>
            <b-table
              show-empty
              responsive
              small
              striped
              hover
              :items="trends"
              :fields="trendsFields"
              :current-page="currentPage"
              :per-page="20"
              :filter="filter"
              @filtered="onFiltered"
            >
              <template
                #cell(module)="row"
              >{{ getModuleFromFullVariable(row.item.item2) }}</template>
              <template
                #cell(object)="row"
              >{{ getObjectFromFullVariable(row.item.item2) }}</template>
              <template #cell(actions)="row" >
                <b-button
                  class="mr-1"
                  size="sm"
                  variant="primary"
                  :title="$t('project.trendsExport.viewGraph')"
                  @click="openTrendGraph(row.item)"
                >
                  <i class="icon-graph" />
                </b-button>
                <b-button
                  class="mr-1"
                  size="sm"
                  variant="secondary"
                  :title="$t('project.trendsExport.addToGraph')"
                  @click="addTrendToGraph(row.item)"
                >
                  <i class="icon-plus" />
                </b-button>
              </template>
            </b-table>
            <b-row>
              <b-col md="6" class="my-1">
                <b-pagination
                  v-model="currentPage"
                  :total-rows="totalRows"
                  :per-page="20"
                  class="my-0"
                ></b-pagination>
              </b-col>
            </b-row>

            <div ref="graphViewer">
              <div class="mt-2" v-if="toDrawGraph">
                <b-card border-variant="secondary" body-class="p-1">
                  <b-row>
                    <b-col sm="5">
                      <h4 class="card-title mb-0">{{ $t('project.graphViewer.title') }}</h4>
                      <div class="small text-muted">{{ toDrawGraph.item4 }}</div>
                    </b-col>
                    <b-col sm="7" class="d-sm-block">
                      <b-button
                        type="button"
                        variant="outline-dark"
                        class="float-right"
                        @click="closeGraphViewer()"
                      >
                        <i class="icon-close"></i>
                      </b-button>
                      <b-button-toolbar class="float-right">
                        <b-form-radio-group
                          class="mr-3"
                          buttons
                          button-variant="outline-secondary"
                          v-model="graphMode"
                        >
                          <b-form-radio class="mx-0" value="day">{{ $t('project.graphViewer.day') }}</b-form-radio>
                          <b-form-radio
                            class="mx-0"
                            value="three"
                          >{{ $t('project.graphViewer.three') }}</b-form-radio>
                          <b-form-radio
                            class="mx-0"
                            value="custom"
                          >{{ $t('project.graphViewer.custom') }}</b-form-radio>
                        </b-form-radio-group>
                      </b-button-toolbar>
                    </b-col>
                  </b-row>
                  <chartManager
                    :variable="toDrawGraph"
                    :projectId="projectId"
                    :graphMode="graphMode"
                    :presetName="presetToReport"
                    :selected-trend-items="selectedTrends"
                    :preset-loading="presetLoading"
                    @clear-selected-trends="clearSelectedTrends"
                    @remove-selected-trend="removeSelectedTrend"
                    ref="chartManager"
                  />
                </b-card>
              </div>
            </div>
          </b-card-body>
        </b-collapse>
      </b-card>
    </div>
    <project-back-button />
  </b-card>
</template>

<script>
import { exportService } from "@/_services";
import { errorHandler } from "@/_helpers";
import { router } from "@/router";
import ChartManager from "@/components/charts/ChartManager";
import ProjectBackButton from "@/components/projects/ProjectBackButton";

export default {
  name: "ProjectTrends",
  components: {
    chartManager: ChartManager,
    projectBackButton: ProjectBackButton
  },
  data() {
    return {
      projectId: null,
      stations: [],
      presets: [],
      errored: null,
      informed: null,
      selectedStations: [],
      presetToReport: "",
      selectedPreset: null,
      presetLoading: false,
      trends: [],
      selectedTrends: [],
      accStations: true,
      accPresets: true,
      accTrends: false,
      trendsFields: [
        { key: "module", label: "" },
        { key: "object", label: "" },
        { key: "item3", label: "" },
        { key: "item4", label: "" },
        { key: "actions", label: "" }
      ],
      currentPage: 1,
      filter: null,
      totalRows: null,
      graphMode: "day",
      toDrawGraph: null
    };
  },
  created() {
    this.projectId = parseInt(this.$route.params.projectId, 10);
    if (isNaN(this.projectId) || this.projectId < 0) {
      router.push("/error404");
      return;
    }

    exportService
      .getStationsForProjects(this.projectId)
      .then(response => {
        this.stations = response.data;
      })
      .catch(error => {
        this.errored = errorHandler(error);
      });

    exportService
      .getPresetsForProjects(this.projectId)
      .then(response => {
        this.presets = response.data;
      })
      .catch(error => {
        this.errored = errorHandler(error);
      });
  },
  methods: {
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    getObjectFromFullVariable(variable) {
      return variable.split(".")[1];
    },
    getModuleFromFullVariable(variable) {
      return variable.split(".")[0];
    },
    closeGraphViewer() {
      this.toDrawGraph = null;
      this.graphMode = "day";
      this.$nextTick(() => {
        var panel = document.getElementById("accordion-trends");
        if (panel && panel.scrollIntoView) {
          panel.scrollIntoView({ block: "start", inline: "nearest" });
        }
      });
    },
    focusGraphViewer() {
      var self = this;
      this.$nextTick(function() {
        if (self.$refs.graphViewer) {
          self.$refs.graphViewer.scrollIntoView({ block: "start", inline: "nearest" });
        }
        self.$nextTick(function() {
          if (self.$refs.chartManager && self.$refs.chartManager.updateFitSize) {
            self.$refs.chartManager.updateFitSize();
          }
        });
      });
    },
    selectStations() {
      this.errored = null;
      this.informed = null;
      if (this.selectedStations.length === 0) return;
      let jsonData = JSON.stringify({ stations: this.selectedStations });
      exportService
        .getTrendsFromStations(this.projectId, jsonData)
        .then(response => {
          this.trends = response.data;
          this.selectedTrends = [];
          this.presetToReport = "";
          this.toDrawGraph = null;
          this.accStations = false;
          this.accPresets = false;
          this.accTrends = true;
        })
        .catch(error => {
          this.errored = errorHandler(error);
        })
        .finally(() => {
          this.totalRows = this.trends.length;
        });
    },
    selectAllStations() {
      if (this.stations.length == this.selectedStations.length) {
        this.selectedStations = [];
        return;
      }
      this.selectedStations = [];
      for (let i = 0; i < this.stations.length; i++) {
        this.selectedStations.push(this.stations[i].item1);
      }
    },
    selectPreset() {
      this.errored = null;
      this.informed = null;
      if (this.selectedPreset === null) return;
      this.presetLoading = true;
      exportService
        .getTrendsFromPreset(this.projectId, this.selectedPreset.id)
        .then((response) => {
          this.trends = response.data;
          this.selectedTrends = response.data;
          this.accStations = false;
          this.accPresets = false;
          this.accTrends = true;
          this.presetToReport = this.selectedPreset.name;
          if (this.trends.length > 0) {
            this.loadTrendGraph(this.trends[0]);
          } else {
            this.toDrawGraph = null;
          }
          var self = this;
          this.$nextTick(function() {
            self.parsePresetConfiguration(self.selectedPreset);
          });
        })
        .catch(error => {
          this.errored = errorHandler(error);
        })
        .finally(() => {
          this.presetLoading = false;
          this.totalRows = this.trends.length;
        });
    },
    loadTrendGraph(trend) {
      this.toDrawGraph = trend;
      this.focusGraphViewer();
    },
    openTrendGraph(trend) {
      if (!trend) return;
      this.selectedTrends = [trend];
      this.toDrawGraph = trend;
      this.focusGraphViewer();
    },
    parsePresetConfiguration(preset) {
      if (preset.configuration === null || preset.configuration === undefined || preset.configuration === "") return;

      const data = JSON.parse(preset.configuration);
      const chartConfig = data.Reports.find(r => r.$type === "Chart");
      if (chartConfig === undefined) return;
      let config = ({ width: chartConfig.Width, height: chartConfig.Height, thresholds: chartConfig.Thresholds, ymin: chartConfig.Ymin, ymax: chartConfig.Ymax })

      if (this.$refs.chartManager === undefined) return;
      this.$refs.chartManager.setChartPreconfiguration(config);
    },
    addTrendToGraph(trend) {
      if (!trend) return;
      var exists = this.selectedTrends.some(function(item) {
        return item.item2 === trend.item2;
      });
      if (!exists) {
        this.selectedTrends = this.selectedTrends.concat([trend]);
      }
      if (!this.toDrawGraph) {
        this.toDrawGraph = trend;
        this.focusGraphViewer();
      }
    },
    clearSelectedTrends() {
      this.selectedTrends = [];
    },
    removeSelectedTrend(variable) {
      this.selectedTrends = this.selectedTrends.filter(function(item) {
        return item.item2 !== variable;
      });
      if (this.selectedTrends.length > 0 && this.toDrawGraph && !this.selectedTrends.some(item => item.item2 === this.toDrawGraph.item2)) {
        this.toDrawGraph = this.selectedTrends[0];
      }
    }
  }
};
</script>

<style scoped>
.trends-tablist {
  margin-left: -1.25rem;
  margin-right: -1.25rem;
}
</style>
